import React from 'react'
import {
  makeStyles,
  ActionButton,
  Alert,
  EstimatorSelect,
  IconButton,
  Typography,
  useMediaQuery,
  useUser
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import type { Company, Contact, Deal } from '@paintscout/api'
import { addAuth0Prefix } from '@paintscout/util'
import type { DashboardEstimator } from '@paintscout/util/builder'
import { useConnection } from 'react-detect-offline'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ContactBar from '../ContactBar/ContactBar'

const SPACE_BETWEEN = 'space-between'

const useStyles = makeStyles<Theme, OwnedHeaderProps>(
  (theme) => ({
    root: {},
    ownedHeaderLg: {
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      alignItems: 'flex-start'
    },
    actionContainer: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      justifyContent: SPACE_BETWEEN
    },
    editIcon: {
      color: theme.paintscout.palette.brand.Neutral.P80,
      marginLeft: theme.spacing(2)
    },
    ownedHeaderSm: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      alignItems: 'center'
    },
    headerContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      '& > h3': {
        fontSize: theme.typography.h4.fontSize
      },
      '&:hover': {
        // target the nested icon, even if not directly nested
        cursor: 'pointer',
        '& svg': {
          color: theme.paintscout.palette.brand.Neutral.P60
        }
      }
    },
    editHeader: {
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      alignItems: 'center'
    },
    editIconMargin: {
      marginRight: `-${theme.spacing(4)}px`
    },
    ownedDropdown: {
      display: 'flex',
      alignItems: 'center'
    },
    profileImage: {
      borderRadius: '50%',
      objectFit: 'cover',
      fontSize: '1.5em',
      width: '1.2em',
      height: '1.2em',
      margin: theme.spacing(),
      marginLeft: 0
    },
    actionButton: {
      marginTop: `${theme.spacing(0.75)}px`,
      '@media print': {
        display: 'none'
      }
    },
    backLink: {
      color: theme.paintscout.palette.brand.Neutral.P50,
      '&:hover': {
        color: theme.palette.primary.main
      }
    },
    contactBar: {
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      alignItems: 'center',
      width: '100%',
      marginTop: theme.spacing(2),
      '& > div': {
        display: 'flex',
        alignItems: 'center'
      }
    },
    nameDiv: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    nameInputDiv: {
      width: '100%',
      display: 'flex',
      alignItems: 'center'
    },
    successButton: {
      color: theme.palette.success.main
    },
    cancelButton: {
      color: theme.palette.error.main
    },
    alertContainer: {
      marginTop: theme.spacing(2),
      marginBottom: `-${theme.spacing()}px`,
      width: '100%'
    },
    alertWithClose: {
      paddingRight: theme.spacing(6)
    }
  }),
  { name: 'OwnedHeader' }
)

export interface OwnedHeaderProps {
  classes?: StyleClasses<typeof useStyles>
  item:
    | Contact
    | Company
    | (Deal & {
        address: string
        city: string
        province: string
        country: string
        postal: string
        phone_number: string
        email: string
        preferred_contact_method: string
      })
  type: 'contact' | 'company' | 'deal'
  actions: { key: string; label: string; value: string }[]
  warning?: string
  canCreateQuotes?: boolean
  canCreateInvoices?: boolean
  hideDisabledActions?: boolean
  onOwnerChange: (estimatorOption: DashboardEstimator) => void
  handleNewClick: (vals: { type: string }) => void
  handleAction: (action: string) => void
  roles?: string[]
  warningOnClose?: () => void
}

function OwnedHeader(props: OwnedHeaderProps) {
  const classes = useStyles(props)
  const {
    item,
    onOwnerChange,
    type,
    handleNewClick,
    actions,
    handleAction,
    warning,
    warningOnClose,
    canCreateQuotes,
    canCreateInvoices,
    hideDisabledActions = false,
    roles = null
  } = props
  const { isCrew } = useUser()
  const { online } = useConnection()
  const name =
    type === 'contact' ? `${(item as Contact)?.first_name} ${(item as Contact)?.last_name}` : (item as Company)?.name

  const isPrint = useMediaQuery('print')
  const isSm = useMediaQuery('sm')
  const isLg = !isSm || isPrint

  return (
    <div className={classes.root}>
      {isLg && (
        <div className={classes.ownedHeaderLg}>
          <div className={classes.nameDiv}>
            <Typography variant="h1" color="textPrimary">
              {`${name}`}
            </Typography>
            <div className={classes.ownedDropdown}>
              <EstimatorSelect
                name="estimators"
                label=""
                fullWidth
                roles={roles}
                variant="single"
                searchable
                value={item?.owner?.id ? addAuth0Prefix(item.owner.id) : null}
                ownedByView
                userFields={['user_metadata.playerName', 'picture', 'user_metadata.customAvatar.value']}
                disabled={!online || isCrew || actions.length === 0}
                hideIcon={!online || isCrew || actions.length === 0}
                onChange={async (estimatorOption: DashboardEstimator) => {
                  // set as item owner and save contact only if new
                  onOwnerChange(estimatorOption)
                }}
                autoApply
                showCollapsibleChips
              />
            </div>
          </div>
          <div className={classes.actionButton}>
            <ActionButton
              data-testid="actions"
              actions={actions}
              disabled={actions.length === 0}
              onActionClick={(_ev, action) => handleAction(action)}
            />
          </div>
        </div>
      )}
      {!isLg && (
        <div className={classes.ownedHeaderSm}>
          <div className={classes.actionContainer}>
            <IconButton onClick={() => window.history.back()}>
              <ArrowBackIcon classes={{ root: classes.backLink }} />
            </IconButton>

            {!isCrew && (
              <ActionButton
                data-testid="actions"
                actions={actions}
                disabled={actions.length === 0}
                onActionClick={(_ev, action) => handleAction(action)}
              />
            )}
          </div>
          <div className={classes.headerContainer}>
            <div className={classes.editHeader}>
              <Typography variant="h1" color="textPrimary" align="center">
                {`${name}`}
              </Typography>
            </div>
            {['contact', 'deal'].includes(type) && (
              <Typography variant="h3" color="textSecondary">
                {(item as Contact)?.company}
              </Typography>
            )}
          </div>
          {warning && (
            <div className={classes.alertContainer} role="status" aria-live="polite">
              <Alert
                className={classes.alertWithClose}
                severity={'warning'}
                variant={'default'}
                content={warning}
                onClose={warningOnClose}
              />
            </div>
          )}
          <ContactBar
            canCreateInvoices={canCreateInvoices}
            canCreateQuotes={canCreateQuotes}
            hideDisabledActions={hideDisabledActions}
            type={type}
            handleNewClick={handleNewClick}
            item={item}
          />
        </div>
      )}
    </div>
  )
}

export default OwnedHeader
